import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useCallback, useEffect } from "react";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { SaidaProduto } from "../../../../domain/models/saida_produtos_lista";
import {
  SaidaProdutoForm,
  SaidaProdutoSchema,
} from "../../../schemas/Entrada_Produtos/saida_produtos";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { DefaultFormNumericInput } from "../../../../../../common/components/default_form_numeric_input";
import { DropDownProdutoServicoDto } from "../../../../../../common/domain/models/dropdown_produto_servico";
import { DropDownProdutoServico } from "../../../../../../common/components/dropdown_produtoservico";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedSaidaProduto: SaidaProduto | undefined;
  centroCustoLista: CentroCustoDropDown[];
  dropdownProdutosServicos: DropDownProdutoServicoDto[];
  onClose: () => void;
  onSubmit: (e: SaidaProdutoForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const SaidaProdutoModal = ({
  open,
  saving,
  loading,
  selectedSaidaProduto,
  centroCustoLista,
  dropdownProdutosServicos,
  onClose,
  onSubmit,
  onOpenChangeHistory,
}: Props) => {
  const hookForm = useForm<SaidaProdutoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(SaidaProdutoSchema),
  });

  const idProdutoServico = hookForm.watch("id_produto_servico");
  useEffect(() => {
    const unidade = dropdownProdutosServicos.find(
      (x) => x.id === idProdutoServico,
    )?.unidade;
    hookForm.setValue("unidade", unidade ?? null);
  }, [hookForm, idProdutoServico, dropdownProdutosServicos]);

  useEffect(() => {
    if (open) {
      hookForm.reset();
      if (selectedSaidaProduto) {
        if (selectedSaidaProduto.Data)
          hookForm.setValue("data", new Date(selectedSaidaProduto.Data));

        hookForm.setValue(
          "id_centro_custo",
          selectedSaidaProduto.IdCentroCusto,
        );
        hookForm.setValue(
          "id_saida_produto",
          selectedSaidaProduto.IdSaidaProduto,
        );
        hookForm.setValue(
          "id_produto_servico",
          selectedSaidaProduto.IdProdutoServico,
        );
        hookForm.setValue("id_fornecedor", selectedSaidaProduto.IdFornecedor);
        hookForm.setValue("nr_nota_fiscal", selectedSaidaProduto.NrNotaFiscal);
        hookForm.setValue("quantidade", selectedSaidaProduto.Quantidade ?? 0);
        hookForm.setValue(
          "valor_unitario",
          selectedSaidaProduto.ValorUnitario ?? null,
        );
        hookForm.setValue(
          "valor_total",
          selectedSaidaProduto.ValorTotal ?? null,
        );
        hookForm.setValue(
          "id_centro_custo_destino",
          selectedSaidaProduto.IdCentroCustoDestino ?? null,
        );
        hookForm.setValue(
          "id_funcionario",
          selectedSaidaProduto.IdFuncionario ?? null,
        );
        hookForm.setValue(
          "ultimoValorUnitario",
          selectedSaidaProduto.UltimoValorUnitario ?? null,
        );
        hookForm.setValue(
          "localAplicacao",
          selectedSaidaProduto.LocalAplicacao ?? null,
        );
        hookForm.setValue("unidade", selectedSaidaProduto.Unidade ?? null);
      }
    } else {
      hookForm.reset({ data: new Date() });
    }
  }, [hookForm, open, selectedSaidaProduto]);

  const handleChangeHistory = useCallback(() => {
    if (selectedSaidaProduto) {
      onOpenChangeHistory(
        selectedSaidaProduto.IdSaidaProduto,
        LogTipoTabela.SAIDA_PRODUTO,
      );
    }
  }, [selectedSaidaProduto, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: hookForm.handleSubmit(onSubmit),
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "1200px", width: "100%" } }}
    >
      <DialogTitle>Saída de Produtos</DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar"} arrangement="fullRow">
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="flex flex-row gap-4 w-full">
              <div className="w-[60%]">
                <DropDownProdutoServico
                  name="id_produto_servico"
                  label="Produto"
                  required={true}
                  reactHookForm={hookForm}
                  tipo="P"
                />
              </div>

              <div className="w-[20%]">
                <FormDatePickerInput
                  name="data"
                  label="Data"
                  reactHookForm={hookForm}
                />
              </div>

              <div className="w-[20%]">
                <DefaultFormInput
                  name="unidade"
                  label="Unidade"
                  readOnly={true}
                  reactHookForm={hookForm}
                />
              </div>
            </div>

            <DropDownFuncionario
              name="id_funcionario"
              label="Solicitante"
              reactHookForm={hookForm}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormAutocomplete
                name="id_centro_custo"
                label="Centro de Custo Saída"
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                required={true}
                onNew={() => {}}
              />
              <DefaultFormAutocomplete
                name="id_centro_custo_destino"
                label="Centro de Custo Destino"
                newButtonLabel="Novo"
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                required={true}
                onNew={() => {}}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormNumericInput
                name="quantidade"
                label="Quantidade"
                required={true}
                reactHookForm={hookForm}
              />
              <DefaultFormInput
                name="nr_nota_fiscal"
                label="N° Nota Fiscal"
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name="valor_unitario"
                label="Valor Unitário (R$)"
                largeNumbers={true}
                required={true}
                reactHookForm={hookForm}
              />
              <FormMoneyMaskedInput
                name="ultimoValorUnitario"
                label="Ultimo Valor Unitário: (R$)"
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name="valor_total"
                label="Valor Total(R$)"
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DefaultFormInput
              name="localAplicacao"
              label="Local de Aplicação"
              reactHookForm={hookForm}
              multiline={true}
              rows={3}
            />
          </div>
          </div>
        </FormContainer>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedSaidaProduto) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
